import { useQuery } from "@tanstack/react-query";
import { apiClient, buildQueryParams } from "@finitless/shared/lib";
import { isEndpointEnabled } from "@finitless/shared/config";

/**
 * Build where clause for logs filtering
 * @param {string} model - Model type (e.g., 'Business')
 * @param {string|number} modelId - Model ID
 * @param {string|number} pluginId - Plugin ID
 * @param {string} relatedGroupEvent - Group key for related events
 * @returns {Object} Where clause object
 */
const buildLogsWhereClause = (model, modelId, pluginId, relatedGroupEvent) => {
  const conditions = [];

  if (model) {
    conditions.push({
      attribute: "model",
      value: model,
    });
  }

  if (modelId) {
    conditions.push({
      attribute: "model_id",
      value: modelId,
    });
  }

  if (pluginId) {
    conditions.push({
      attribute: "plugin_id",
      value: pluginId,
    });
  }

  if (relatedGroupEvent) {
    conditions.push({
      attribute: "group_key",
      value: relatedGroupEvent,
    });
  }

  return conditions.length > 0
    ? {
        conditions,
        conector: "AND",
      }
    : {};
};

/**
 * Hook to fetch integration logs
 * @param {Object} params - Query parameters
 * @param {string} params.model - Model type
 * @param {string|number} params.modelId - Model ID
 * @param {string|number} params.pluginId - Plugin ID
 * @param {number} params.page - Page number
 * @param {number} params.pageSize - Page size
 * @param {string} params.relatedGroupEvent - Group key for related events
 * @param {boolean} params.enabled - Whether the query should be enabled
 * @returns {Object} Query result with logs and pagination
 */
export const useIntegrationLogs = (params = {}) => {
  const {
    model,
    modelId,
    pluginId,
    page = 1,
    pageSize = 10,
    relatedGroupEvent = null,
    enabled = true,
  } = params;

  const whereClause = buildLogsWhereClause(
    model,
    modelId,
    pluginId,
    relatedGroupEvent,
  );

  const queryParams = {
    orderBy: "-id",
    page,
    page_size: pageSize,
    params: "model,model_id,event,type,created_at,group_key,id",
    ...(Object.keys(whereClause).length > 0 && { where: whereClause }),
  };

  const queryString = buildQueryParams(queryParams);

  return useQuery({
    queryKey: [
      "integrationLogs",
      model,
      modelId,
      pluginId,
      page,
      pageSize,
      relatedGroupEvent,
    ],
    queryFn: async () => {
      const response = await apiClient.get(
        `/logs${queryString ? `?${queryString}` : ""}`,
      );

      return {
        logs: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    enabled: isEndpointEnabled("logs", "list") && enabled,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch individual log details
 * @param {string|number} logId - Log ID
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {Object} Query result with log details
 */
export const useIntegrationLogDetail = (logId, enabled = true) => {
  return useQuery({
    queryKey: ["integrationLogDetail", logId],
    queryFn: async () => {
      if (!logId) return null;
      const response = await apiClient.get(`/logs/${logId}`);
      return response?.result || null;
    },
    enabled: isEndpointEnabled("logs", "detail") && enabled && !!logId,
    staleTime: 0,
  });
};

export default {
  useIntegrationLogs,
  useIntegrationLogDetail,
};
